import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Loader2, X, UploadCloud } from "lucide-react";
import { adminApi } from "../api";

/**
 * Video uploader for product galleries & home sections.
 * Uploads directly to Cloudinary using signed params (resource_type=video).
 */
const MAX_MB_DEFAULT = 80;

export default function VideoUploader({
  value = [],
  onChange,
  folder = "asvasa/videos",
  max = 3,
  maxSizeMB = MAX_MB_DEFAULT,
  testid = "video-uploader",
  single = false,
}) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);

  const videos = Array.isArray(value)
    ? value.filter(Boolean)
    : value
    ? [value]
    : [];

  const getSig = useCallback(async () => {
    const { data } = await adminApi.get(
      `/admin/uploads/signature?folder=${encodeURIComponent(folder)}&resource_type=video`
    );
    return data;
  }, [folder]);

  const uploadOne = useCallback(async (file, sig, tempId) => {
    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setProgress((p) => ({ ...p, [tempId]: pct }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.send(form);
    });
  }, []);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) => f.type.startsWith("video/"));
      if (!files.length) return;
      // size guard
      const oversize = files.find((f) => f.size > maxSizeMB * 1024 * 1024);
      if (oversize) {
        toast.error(`"${oversize.name}" is larger than ${maxSizeMB} MB.`);
        return;
      }
      const remaining = Math.max(0, max - videos.length);
      const toUpload = files.slice(0, remaining || 1);
      setUploading(true);
      try {
        const sig = await getSig();
        const uploaded = [];
        for (const file of toUpload) {
          const tempId = `${file.name}-${Date.now()}`;
          setProgress((p) => ({ ...p, [tempId]: 1 }));
          try {
            const res = await uploadOne(file, sig, tempId);
            uploaded.push(res.secure_url);
            setProgress((p) => {
              const np = { ...p };
              delete np[tempId];
              return np;
            });
          } catch {
            toast.error(`Failed to upload ${file.name}`);
            setProgress((p) => {
              const np = { ...p };
              delete np[tempId];
              return np;
            });
          }
        }
        if (uploaded.length) {
          const next = single ? [uploaded[0]] : [...videos, ...uploaded];
          onChange(single ? next[0] : next);
          toast.success(`${uploaded.length} video${uploaded.length > 1 ? "s" : ""} uploaded`);
        }
      } catch {
        toast.error("Could not start upload. Please retry.");
      } finally {
        setUploading(false);
      }
    },
    [videos, max, maxSizeMB, getSig, uploadOne, onChange, single]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const remove = (idx) => {
    if (single) {
      onChange("");
      return;
    }
    const next = videos.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        className={`cursor-pointer border-2 border-dashed transition-colors px-6 py-10 text-center ${
          dragOver ? "border-[#c3ba9e] bg-[#1f1f21]" : "border-[#2a2a2d] bg-[#0d0d0e] hover:border-[#c3ba9e]/50"
        }`}
        data-testid={`${testid}-dropzone`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-[#c3ba9e]">
            <Loader2 className="animate-spin" size={22} />
            <p className="text-[11px] tracking-[0.28em] uppercase">Uploading video…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-[#8c8a7e]">
            <UploadCloud size={26} />
            <p className="text-[12px] tracking-wider">
              Drag & drop or <span className="text-[#c3ba9e] underline">browse video</span>
            </p>
            <p className="text-[10px] text-[#5e5d54]">MP4, MOV, WEBM · max {maxSizeMB} MB · {single ? "single video" : `up to ${max} videos`}</p>
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          accept="video/*"
          multiple={!single}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testid}-input`}
        />
      </div>

      {Object.entries(progress).length > 0 && (
        <div className="mt-3 space-y-1.5">
          {Object.entries(progress).map(([id, pct]) => (
            <div key={id} className="h-1 bg-[#1f1f21] overflow-hidden">
              <div className="h-full bg-[#c3ba9e] transition-all" style={{ width: `${pct}%` }} />
            </div>
          ))}
        </div>
      )}

      {videos.length > 0 && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testid}-list`}>
          {videos.map((url, i) => (
            <div key={`${url}-${i}`} className="relative group bg-[#0d0d0e] border border-[#2a2a2d]" data-testid={`${testid}-item-${i}`}>
              <video src={url} className="w-full h-auto aspect-video object-cover" controls muted />
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute top-1.5 right-1.5 bg-[#0d0d0e]/85 text-[#e9e6db] p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testid}-remove-${i}`}
              >
                <X size={12} />
              </button>
              <p className="px-3 py-2 text-[10px] text-[#8c8a7e] flex items-center gap-2">
                <Film size={11} /> Video {i + 1}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
